import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNotification } from 'components/Notification'
import {
  getTearsheetCustomTemplates,
  createTearsheetCustomTemplate,
  updateTearsheetCustomTemplate,
  deleteTearsheetCustomTemplate,
} from 'services/tearsheets'

/**
 * @param {Object} options
 * @param {string} [options.customerId] - scopes CRUD to a specific customer when provided
 * @param {boolean} [options.isGlobal=false] - when true, operates on templates with no
 *   customer association (customer_id IS NULL); query fires immediately without a customerId
 */
const useTearsheetTemplates = ({ customerId, isGlobal = false }) => {
  const queryClient = useQueryClient()
  const { createNotification } = useNotification()

  const queryKey = ['getTearsheetCustomTemplates', isGlobal ? 'global' : customerId]

  const { data: tearsheetTemplates, isFetching: isFetchingTearsheetTemplates } = useQuery({
    queryFn: () => getTearsheetCustomTemplates(isGlobal ? { scope: 'global' } : { customerId }),
    queryKey,
    enabled: isGlobal || !!customerId,
    initialData: [],
  })

  const { mutate: createTemplate, isPending: isCreateTemplateLoading } = useMutation({
    mutationFn: params =>
      createTearsheetCustomTemplate(isGlobal ? params : { ...params, customerId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      createNotification({
        message: 'Template created successfully',
        variant: 'success',
      })
    },
    onError: () => {
      createNotification({
        message: 'Failed to create template',
        variant: 'error',
      })
    },
  })

  const { mutate: updateTemplate, isPending: isUpdateTemplateLoading } = useMutation({
    mutationFn: updateTearsheetCustomTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      createNotification({
        message: 'Template updated successfully',
        variant: 'success',
      })
    },
    onError: () => {
      createNotification({
        message: 'Failed to update template',
        variant: 'error',
      })
    },
  })

  const { mutate: deleteTemplate, isPending: isDeleteTemplateLoading } = useMutation({
    mutationFn: deleteTearsheetCustomTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      createNotification({
        message: 'Template deleted successfully',
        variant: 'success',
      })
    },
    onError: () => {
      createNotification({
        message: 'Failed to delete template',
        variant: 'error',
      })
    },
  })

  return {
    tearsheetTemplates,
    isFetchingTearsheetTemplates,
    createTemplate,
    isCreateTemplateLoading,
    updateTemplate,
    isUpdateTemplateLoading,
    deleteTemplate,
    isDeleteTemplateLoading,
  }
}

export default useTearsheetTemplates
